// Camera capture with live preview and simple document auto-crop hint.
// Exposes: window.OcrCamera = { open(el, opts), close(), capture(), switchCamera(), autoCropDocument, describeError }
(function () {
  'use strict';

  let stream = null;
  let currentFacing = 'environment';
  let videoEl = null;
  let guideEl = null;
  let detectTimer = null;

  async function open(opts) {
    opts = opts || {};
    videoEl = opts.video || document.querySelector('[data-camera-video]');
    guideEl = opts.guide || document.querySelector('[data-camera-guide]');
    if (!videoEl) throw new Error('No video element');
    await startStream(currentFacing);
    startEdgeDetection();
  }

  // Through the shared camera door when the page carries it: it guards a
  // getUserMedia that never answers, CLASSIFIES a refusal and has the sentence
  // for each cause in every locale (describeError below hands it to the page).
  // And frames, not play(): on a camera that never sends one, play() never
  // settles and the camera panel stayed open over a black box. After 6 s the
  // door says why and keeps waiting; a camera that never comes up is thrown as
  // a refusal the page can name ('unresponsive').
  let camMgr = null;
  async function startStream(facing) {
    if (stream) stopStream();
    const want = {
      video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1440 } },
      audio: false,
    };
    try {
      if (window.CameraManager && !camMgr) camMgr = new window.CameraManager();
      stream = camMgr ? await camMgr.startRaw(want) : await window.__tmbGum(want);
      let framed = true;
      if (camMgr) framed = await camMgr.show(videoEl, stream);
      else if (window.__cameraFrames) framed = await window.__cameraFrames(videoEl, { stream: stream });
      else { videoEl.srcObject = stream; const p = videoEl.play(); if (p && p.catch) p.catch(() => {}); }
      if (!framed) {
        const none = new Error('the camera sends no frames');
        none.code = 'unresponsive';
        throw none;
      }
      currentFacing = facing;
    } catch (err) {
      stopStream();
      throw err;
    }
  }

  // Why the camera did not open, and the sentence for it — null when the door is
  // not on the page (the caller keeps its own wording then).
  function describeError(err) {
    if (!window.CameraManager) return null;
    const code = (err && err.code) || window.CameraManager.classify(err);
    try {
      if (!camMgr) camMgr = new window.CameraManager();
      return { code: code, message: camMgr.getErrorMessage(code) };
    } catch (e) { return null; }
  }

  function stopStream() {
    if (detectTimer) { clearInterval(detectTimer); detectTimer = null; }
    if (camMgr) { try { camMgr.stop(); } catch (e) {} }
    if (stream) {
      stream.getTracks().forEach(t => { try { t.stop(); } catch (e) {} });
      stream = null;
    }
    if (videoEl) { try { videoEl.pause(); videoEl.srcObject = null; } catch (e) {} }
  }

  function close() {
    stopStream();
    if (guideEl) guideEl.classList.remove('detected');
  }

  async function switchCamera() {
    currentFacing = currentFacing === 'environment' ? 'user' : 'environment';
    await startStream(currentFacing);
  }

  // Capture current frame → Canvas
  function capture() {
    if (!videoEl || !videoEl.videoWidth) return null;
    const canvas = document.createElement('canvas');
    canvas.width = videoEl.videoWidth;
    canvas.height = videoEl.videoHeight;
    if (!window.__drawCameraFrame(canvas.getContext('2d'), videoEl, 0, 0)) return null;   // an interrupted camera
    return canvas;
  }

  // Rough edge-strength indicator — changes guide colour when a rectangular
  // dark object (document) is roughly aligned with the guide frame.
  function startEdgeDetection() {
    if (detectTimer) clearInterval(detectTimer);
    const tmp = document.createElement('canvas');
    const tctx = tmp.getContext('2d', { willReadFrequently: true });
    detectTimer = setInterval(() => {
      if (!videoEl || !videoEl.videoWidth) return;
      tmp.width = 160; tmp.height = 120;
      if (!window.__drawCameraFrame(tctx, videoEl, 0, 0, 160, 120)) return;   // no frame this tick
      const data = tctx.getImageData(0, 0, 160, 120).data;
      // Measure contrast inside the guide region (middle ~60%)
      let insideDark = 0, insideBright = 0, count = 0;
      for (let y = 24; y < 96; y += 3) {
        for (let x = 16; x < 144; x += 3) {
          const i = (y * 160 + x) * 4;
          const g = (data[i] + data[i + 1] + data[i + 2]) / 3;
          if (g < 80) insideDark++;
          else if (g > 180) insideBright++;
          count++;
        }
      }
      const contrast = (insideBright - insideDark) / count;
      if (guideEl) {
        if (insideBright / count > 0.4 && contrast > 0.15) guideEl.classList.add('detected');
        else guideEl.classList.remove('detected');
      }
    }, 400);
  }

  // Simple heuristic autocrop: find largest non-background region and crop to it.
  // For real perspective correction we would need OpenCV; this is a pragmatic
  // fallback that works on well-lit backgrounds.
  function autoCropDocument(canvas) {
    try {
      const w = canvas.width, h = canvas.height;
      const ctx = canvas.getContext('2d');
      const img = ctx.getImageData(0, 0, w, h);
      const d = img.data;
      const corner = ((d[0] + d[1] + d[2]) / 3);
      const threshold = corner > 128 ? corner - 40 : corner + 40;
      const darker = corner > 128;
      let minX = w, minY = h, maxX = 0, maxY = 0, found = false;
      const step = Math.max(2, Math.floor(w / 500));
      for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
          const i = (y * w + x) * 4;
          const g = (d[i] + d[i + 1] + d[i + 2]) / 3;
          const ok = darker ? g < threshold : g > threshold;
          if (ok) {
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
            found = true;
          }
        }
      }
      if (!found) return canvas;
      const pad = 16;
      minX = Math.max(0, minX - pad);
      minY = Math.max(0, minY - pad);
      maxX = Math.min(w - 1, maxX + pad);
      maxY = Math.min(h - 1, maxY + pad);
      const cw = maxX - minX + 1, ch = maxY - minY + 1;
      // Only crop if we removed a meaningful margin (>5% on every side)
      if (cw > w * 0.94 && ch > h * 0.94) return canvas;
      if (cw < w * 0.3 || ch < h * 0.3) return canvas; // suspicious
      const out = document.createElement('canvas');
      out.width = cw; out.height = ch;
      out.getContext('2d').drawImage(canvas, -minX, -minY);
      return out;
    } catch (e) { return canvas; }
  }

  window.OcrCamera = { open, close, switchCamera, capture, autoCropDocument, describeError };
})();
